/* eslint-disable jsx-a11y/alt-text */

import React, { useEffect, useState } from "react";
import Container from "../../../utils/Container";
import { useParams } from "react-router";
import { hotelDetails } from "../../../api/website/hotel/hotelApi";
import { Tooltip } from "@mui/joy";
import { FcApproval } from "react-icons/fc";
import { GoStarFill } from "react-icons/go";
import { CiLocationOn } from "react-icons/ci";
import {
  FaBabyCarriage,
  FaBed,
  FaCalendarCheck,
  FaCalendarTimes,
  FaCat,
  FaHeart,
  FaHotel,
  FaRegAddressCard,
} from "react-icons/fa";
import DhoorbinChoice from "../../../assets/icons/dhoorbinchoice.svg";
import { Swiper, SwiperSlide } from "swiper/react";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import { FaBedPulse } from "react-icons/fa6";
import {
  MdFastfood,
  MdOutlineBathroom,
  MdOutlineBedroomParent,
  MdOutlinePets,
  MdOutlineViewModule,
  MdPayment,
} from "react-icons/md";
import { SlDirections } from "react-icons/sl";
import AddButton from "../../../components/admin/usable/AddButton";
import BookedHotel from "./sections/BookedHotel";
import { useDispatch, useSelector } from "react-redux";
import {
  setHotelBook,
  setHotelinfo,
} from "../../../redux/website/booking/BookingSlice";
import { GiPathDistance } from "react-icons/gi";
import IcCash from "../../../assets/icons/payment/cash.svg";
import IcDebit from "../../../assets/icons/payment/debit.svg";
import IcMasterCard from "../../../assets/icons/payment/mastercard.svg";
import IcVisacard from "../../../assets/icons/payment/visacard.svg";
import IcAmmex from "../../../assets/icons/payment/ammex.svg";
import RoomDetails from "./sections/RoomDetails";
import HotelLoader from "./sections/HotelLoader";
import FloatingBookMenu from "./sections/FloatingBookMenu";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import UniversalSearch from "../../../utils/universalSearch/UniversalSearch";
import useSEO from "../../../utils/seo";

// ============================
// DATE HELPERS
// ============================
function toDateKey(date) {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// ============================
// PER-NIGHT PRICE BREAKDOWN
// ============================
// Builds one entry per night between checkIn (inclusive) and checkOut (exclusive).
// Each night uses that date's own price/discount from availability.
// Falls back to the room's base actualPrice/discount when a date has no record.
const buildNightlyBreakdown = (room, checkIn, checkOut) => {
  if (!checkIn || !checkOut) {
    return {
      nights: [],
      totalNights: 0,
      subtotal: 0,
      taxTotal: 0,
      grandTotal: 0,
      isUniform: true,
      hasUnavailable: false,
    };
  }

  const availability = room.availability || [];
  const availMap = {};
  availability.forEach((a) => {
    availMap[toDateKey(a.date)] = a;
  });

  const taxRate = Number(room.tax || 0);

  const start = new Date(checkIn);
  const end = new Date(checkOut);

  const nights = [];
  let hasUnavailable = false;

  const cursor = new Date(start);
  while (cursor < end) {
    const key = toDateKey(cursor);
    const record = availMap[key];

    // price/discount for this night (fallback to room base)
    const basePrice =
      record && record.price !== null && record.price !== undefined
        ? Number(record.price)
        : Number(room.actualPrice || 0);

    const discount =
      record && record.discount !== null && record.discount !== undefined
        ? Number(record.discount)
        : Number(room.discount || 0);

    // a night is unavailable only if a record says so
    const nightUnavailable =
      record && (record.isAvailable === 0 || record.rooms === 0);

    if (nightUnavailable) hasUnavailable = true;

    const discountedPrice = basePrice * (1 - discount / 100);
    const nightTax = discountedPrice * (taxRate / 100);

    nights.push({
      date: key,
      basePrice,
      discount,
      discountedPrice,
      tax: nightTax,
    });

    cursor.setDate(cursor.getDate() + 1);
  }

  const subtotal = nights.reduce((sum, n) => sum + n.discountedPrice, 0);
  const taxTotal = nights.reduce((sum, n) => sum + n.tax, 0);

  // uniform = every night has the same price AND the same discount
  const isUniform =
    nights.length > 0 &&
    nights.every(
      (n) =>
        n.basePrice === nights[0].basePrice &&
        n.discount === nights[0].discount,
    );

  return {
    nights,
    totalNights: nights.length,
    subtotal,
    taxTotal,
    grandTotal: subtotal + taxTotal,
    isUniform,
    hasUnavailable,
    segments: groupNightsIntoSegments(nights),
  };
};

// ============================
// GROUP NIGHTS INTO SEGMENTS
// ============================
// Collapses consecutive nights that share the same price+discount into
// one row, so the table shows ranges instead of every single date.
const groupNightsIntoSegments = (nights = []) => {
  const segments = [];

  nights.forEach((night) => {
    const last = segments[segments.length - 1];

    if (
      last &&
      last.basePrice === night.basePrice &&
      last.discount === night.discount
    ) {
      last.endDate = night.date;
      last.count += 1;
      last.subtotal += night.discountedPrice;
    } else {
      segments.push({
        startDate: night.date,
        endDate: night.date,
        basePrice: night.basePrice,
        discount: night.discount,
        discountedPrice: night.discountedPrice,
        count: 1,
        subtotal: night.discountedPrice,
      });
    }
  });

  return segments;
};

// ============================
// AVAILABILITY HELPER
// ============================
const isRoomUnavailable = (availability = [], checkIn, checkOut) => {
  if (!checkIn || !checkOut || availability.length === 0) return false;

  const start = new Date(checkIn);
  const end = new Date(checkOut);

  return availability.some((a) => {
    if (a.isAvailable !== 0 || a.rooms !== 0) return false;
    const d = new Date(a.date);
    return d >= start && d < end;
  });
};

const Hotel = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [HotelData, setHotelData] = useState("");
  const [Isloading, setIsloading] = useState(false);
  const { bookedHotel } = useSelector((state) => state.booked);
  const [RoomInfo, setRoomInfo] = useState("");
  const [open, setOpen] = useState(false);
  const { checkIn, checkOut } = useSelector((state) => state.search);
  const [OpenImageViewer, setOpenImageViewer] = useState(false);
  const Rooms = useSelector((state) => state.search.room);
  useEffect(() => {
    hotelDetails(id, setHotelData, setIsloading);
  }, [id]);
  const handleBook = (item, breakdown) => {
    dispatch(
      setHotelinfo({
        hotelId: HotelData.id,
        hotelName: HotelData.hotelName,
        hotelLogo: HotelData.hotelLogo,
        images: HotelData.images,
        commissionRate: HotelData.advancePayment,
        checkIn: HotelData.checkIn,
        checkOut: HotelData.checkOut,
      }),
    );

    // Build the room object that goes into the cart.
    // BookedHotel multiplies discountedPrice * nights, so for a mixed-price
    // stay we send a per-night average (subtotal / nights) that reproduces
    // the correct stay total when multiplied back. We also attach the raw
    // stay totals, segments, and the full per-night list so later steps
    // (checkout, backend, invoice) can read/store the exact figures.
    let bookedItem = { ...item };

    if (breakdown && breakdown.totalNights > 0) {
      // nightlyBreakdown: one row per night with that night's own numbers.
      const nightlyBreakdown = (breakdown.nights || []).map((n) => ({
        date: n.date,
        actualPrice: n.basePrice,
        discount: n.discount,
        discountedPrice: n.discountedPrice,
        tax: Number(item.tax || 0),
        taxAmount: n.tax,
      }));

      bookedItem = {
        ...item,
        // for mixed pricing, send per-night average so existing
        // discountedPrice * nights math stays correct; uniform keeps its own
        discountedPrice: breakdown.isUniform
          ? item.discountedPrice
          : breakdown.subtotal / breakdown.totalNights,
        stayTotal: breakdown.subtotal,
        stayTax: breakdown.taxTotal,
        stayGrandTotal: breakdown.grandTotal,
        stayNights: breakdown.totalNights,
        priceSegments: breakdown.segments,
        nightlyBreakdown,
      };
    }

    dispatch(setHotelBook([...bookedHotel, bookedItem]));
  };

  const nights =
    (new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24);
  const handleOpenDrawer = (item) => {
    setRoomInfo(item);
    setOpen(!open);
  };
  const TotalRoom = Rooms.reduce((total) => total + 1, 0);
  const TotalGuest = Rooms.reduce(
    (total, room) => total + room.adult + room.children,
    0,
  );
  const totalChildren = Rooms.reduce((total, room) => total + room.children, 0);
  const totalAdult = Rooms.reduce((total, room) => total + room.adult, 0);
  const sortedRooms =
    HotelData?.roomsData?.length > 0
      ? [...HotelData.roomsData].sort((a, b) => {
          const aMatch = a.adults >= totalAdult;
          const bMatch = b.adults >= totalAdult;

          if (aMatch && !bMatch) return -1;
          if (!aMatch && bMatch) return 1;
          if (!aMatch && !bMatch) return -1;
          return 0;
        })
      : [];
  useSEO({
    title: `${HotelData.hotelName || ""} | Dhoorbin.com`,
    description: `${HotelData.description}`,
    image: `${window.location.origin}/images/banner.png`,
    url: "https://dhoorbin.com",
    canonical: "https://dhoorbin.com",
    keywords: "travel, hotel, tour, booking",
    publisher: "Dhoorbin",
    robots: "index, follow",
    lang: "en",
  });
  return (
    <>
      {open && <RoomDetails data={RoomInfo} open={open} setOpen={setOpen} />}
      <FloatingBookMenu />
      {OpenImageViewer && (
        <>
          {HotelData?.images && (
            <Lightbox
              open={OpenImageViewer}
              close={() => setOpenImageViewer(false)}
              styles={{
                root: { "--yarl__color_backdrop": "rgba(0, 0, 0, .7)" },
              }}
              slides={HotelData.images.map((img) => ({
                src: img.imageLink,
                alt: "Hotel Image",
              }))}
            />
          )}
        </>
      )}
      <Container>
        {Isloading === true ? (
          <div className="py-4 lg:py-10 px-0 lg:px-10">
            <HotelLoader />
          </div>
        ) : (
          <>
            {HotelData && (
              <>
                <div className="py-4 lg:py-10 px-0 lg:px-10 space-y-10 mb-20 lg:mb-0">
                  <div className="grid grid-col-1 lg:grid-cols-3 gap-3 border p-3 rounded-lg">
                    <div className="block lg:flex gap-3 col-span-0 lg:col-span-2 ">
                      <div className="flex-1">
                        <div
                          onClick={() => setOpenImageViewer(true)}
                          className="w-full h-[400px] overflow-hidden rounded-md border cursor-pointer"
                        >
                          <img
                            className="w-full h-full object-cover"
                            src={HotelData?.images[0]?.imageLink}
                            alt={HotelData.hotelName}
                          />
                        </div>
                      </div>
                      <div className="pt-3 lg:pt-0 flex flex-row lg:flex-col gap-3">
                        {HotelData?.images?.slice(1, 5).map((item, index) => {
                          return (
                            <div
                              onClick={() => setOpenImageViewer(true)}
                              key={index}
                              className="w-[50px] lg:w-[70px] h-[50px] lg:h-[70px] overflow-hidden rounded-md cursor-pointer"
                            >
                              <img
                                className="w-full h-full object-cover"
                                src={item?.imageLink}
                                alt={HotelData?.hotelName}
                              />
                            </div>
                          );
                        })}

                        {HotelData?.images?.length > 5 && (
                          <div
                            onClick={() => setOpenImageViewer(true)}
                            className="w-[50px] lg:w-[70px] h-[50px] lg:h-[70px] overflow-hidden rounded-md relative cursor-pointer"
                          >
                            <img
                              className="w-full h-full object-cover"
                              src={HotelData?.images[5]?.imageLink}
                              alt={HotelData?.hotelName}
                            />

                            <div className="absolute inset-0 bg-black/60 flex justify-center items-center">
                              <p className="text-xs lg:text-sm text-white font-semibold">
                                +{HotelData.images.length - 5}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="space-y-8">
                      <div className="flex flex-col justify-start gap-x-1">
                        <div className="flex justify-start items-center gap-3">
                          <p className="py-2 font-bold text-md lg:text-xl text-[#2155ad] capitalize bg-gradient-to-t from-blue-500 to-blue-900 bg-clip-text text-transparent">
                            {HotelData.hotelName}
                          </p>
                          {HotelData.isFeatured && (
                            <div className="cursor-pointer">
                              <Tooltip
                                title="Dhoorbin Featured"
                                placement="top"
                                size="sm"
                              >
                                <FcApproval />
                              </Tooltip>
                            </div>
                          )}
                        </div>

                        <div className="flex justify-start items-center gap-x-3">
                          <div className="flex justify-start items-center gap-1 text-xs text-yellow-600 bg-yellow-200 p-1 rounded-md">
                            <div>
                              <GoStarFill />
                            </div>
                            <div>
                              <p>{HotelData.propertyStar}</p>
                            </div>
                          </div>
                          <div className="flex justify-start items-center gap-1">
                            <div>
                              <CiLocationOn />
                            </div>
                            <p className="text-xs text-black/50 break-words">
                              {HotelData.area}, {HotelData.cityName},{" "}
                              {HotelData.divisionName}, {HotelData.countryName}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div>
                        <div>
                          <p className="py-0 lg:py-2 font-bold text-sm lg:text-md text-[#2155ad] capitalize bg-gradient-to-t from-blue-500 to-blue-900 bg-clip-text text-transparent">
                            Facilities
                          </p>
                        </div>
                        <div className="py-1 lg:py-0 flex gap-2 flex-wrap justify-start items-center">
                          {HotelData?.facilities
                            ?.slice(0, 2)
                            .map((group, groupIndex) => (
                              <div
                                key={groupIndex}
                                className="flex justify-start items-center gap-2 flex-wrap"
                              >
                                {group.facilities
                                  .slice(0, 3)
                                  .map((f, index) => (
                                    <div
                                      key={index}
                                      className="flex flex-wrap justify-start items-center gap-1"
                                    >
                                      <div className="w-4 h-4 overflow-hidden flex justify-center items-center">
                                        <img
                                          className="w-full h-full object-contain"
                                          src={f.icon}
                                          alt={f.facilityName}
                                        />
                                      </div>
                                      <div>
                                        <p className="text-xs text-black/50">
                                          {f.facilityName}
                                        </p>
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            ))}
                        </div>
                      </div>
                      <div className="flex justify-start gap-3 items-center">
                        {HotelData.isIdRequired && (
                          <div className="flex justify-star items-center gap-1 text-black/50">
                            <div>
                              <FaRegAddressCard />
                            </div>
                            <div>
                              <p className="text-xs text-black/50">
                                ID Required
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                      <div>
                        {HotelData.isCoupleFriendly && (
                          <div className=" text-pink-500 bg-pink-200 border border-pink-500 px-2 py-0.5 rounded-sm cursor-pointer w-fit">
                            <Tooltip
                              title={
                                <div className="p-2">
                                  <p className="text-[10px]">
                                    No marrige certificate required
                                  </p>
                                  <p className="text-[10px]">
                                    Safety and comfort ensured
                                  </p>
                                  <p className="text-[10px]">
                                    Friendly Environment
                                  </p>
                                </div>
                              }
                              placement="right"
                              size="sm"
                            >
                              <div className="flex items-center justify-start gap-x-2">
                                <div className="text-xs">
                                  <FaHeart />
                                </div>
                                <p className="text-xs font-semibold">
                                  Couple Friendly
                                </p>
                              </div>
                            </Tooltip>
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="flex items-start justify-start gap-2 border p-2 rounded-lg">
                          <div className="w-40 h-20">
                            <img
                              className="w-full h-full object-contain"
                              src={DhoorbinChoice}
                              alt=""
                            />
                          </div>
                          <div>
                            <p className="text-md font-bold ">
                              Dhoorbin Choice
                            </p>
                            <p className="text-xs text-justify text-black/70">
                              Dhoorbin Choice means this property is inspected
                              by Dhoorbin and marked safe and enjoyable for
                              travellers, we recommended.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="border p-3 rounded-lg">
                    <div className="flex lg:flex-row flex-col justify-start gap-3">
                      <div className="w-full lg:w-40 h-20 py-2 lg:py-0 overflow-hidden rounded-md flex-shrink-0">
                        <img
                          className="w-full h-full object-contain lg:object-contain"
                          src={HotelData.hotelLogo}
                          alt={HotelData.hotelName}
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-center lg:justify-start items-center gap-3">
                          <p className="text-xs lg:text-sm text-black/60">
                            <strong>Rooms:</strong> {HotelData.rooms || 0}
                          </p>
                          <p className="text-xs lg:text-sm text-black/60">
                            <strong>Floors:</strong> {HotelData.floors || 0}
                          </p>
                          <p className="text-xs lg:text-sm text-black/60">
                            <strong>Founded Year:</strong>{" "}
                            {HotelData.foundedYear || 0}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-justify text-black/50">
                            {HotelData.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="border p-3 rounded-lg ">
                    <UniversalSearch IsShowHotel={false} />
                  </div>
                  <div className="p-3 rounded-lg">
                    <div className="block lg:flex gap-3">
                      <div className="flex-1 border spacey-3">
                        <div className="w-full h-auto p-3 bg-[#a9bffa]">
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-bold">Room Details</p>
                            </div>
                            <div>
                              <p>
                                {nights > 1 ? `${nights} Nights` : `1 Night`}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div>
                          {sortedRooms.length > 0 ? (
                            <>
                              {sortedRooms.map((item, index) => {
                                const unavailable = isRoomUnavailable(
                                  item.availability,
                                  checkIn,
                                  checkOut,
                                );
                                const isAlreadyBooked = bookedHotel.some(
                                  (bookedItem) => bookedItem.id === item.id,
                                );

                                // per-night price breakdown for this stay
                                const breakdown = buildNightlyBreakdown(
                                  item,
                                  checkIn,
                                  checkOut,
                                );
                                const segments = groupNightsIntoSegments(
                                  breakdown.nights,
                                );
                                // prices differ across the stay
                                const hasMixedPricing =
                                  breakdown.totalNights > 0 &&
                                  !breakdown.isUniform;

                                return (
                                  <div key={index}>
                                    <div className="block  lg:flex gap-3 p-3 border-b">
                                      <div className="w-full lg:w-[350px] flex-shrink-0 h-[280px] overflow-hidden rounded-lg">
                                        <Swiper
                                          autoplay={{
                                            delay: 2500,
                                            disableOnInteraction: false,
                                          }}
                                          navigation={true}
                                          modules={[Autoplay, Navigation]}
                                          loop={true}
                                          className="border"
                                        >
                                          {item.images.map((item, index) => {
                                            return (
                                              <SwiperSlide key={index}>
                                                <div className="w-full lg:w-[350px] h-[280px] overflow-hidden rounded-lg">
                                                  <img
                                                    className="w-full h-full object-cover"
                                                    src={item.imageLink}
                                                    alt={HotelData.hotelName}
                                                  />
                                                </div>
                                              </SwiperSlide>
                                            );
                                          })}
                                        </Swiper>
                                      </div>
                                      <div className="space-y-3 pt-3 lg:pt-0">
                                        <div className="flex justify-start items-center gap-2">
                                          <p className="font-bold text-lg">
                                            {item.roomName}{" "}
                                            <span className="text-xs font-normal text-black/50">
                                              ({item.roomType})
                                            </span>
                                          </p>

                                          {unavailable && (
                                            <span className="text-xs font-semibold text-white bg-red-500 px-2 py-0.5 rounded-md">
                                              Not Available
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex flex-wrap justify-start items-center gap-3">
                                          <div className="flex justify-start items-center gap-1 text-xs text-black/50">
                                            <FaBed />{" "}
                                            <p>Bed Size: {item.bedSize}</p>
                                          </div>
                                          <div className="flex justify-start items-center gap-1 text-xs text-black/50">
                                            <FaBedPulse />{" "}
                                            <p>Beds: {item.beds}</p>
                                          </div>
                                          <div className="flex justify-start items-center gap-1 text-xs text-black/50">
                                            <MdOutlineBedroomParent />{" "}
                                            <p>
                                              Room Size: {item.roomSize}/sqf
                                            </p>
                                          </div>
                                          <div className="flex justify-start items-center gap-1 text-xs text-black/50">
                                            <MdOutlineBathroom />{" "}
                                            <p>Bathrooms: {item.bathrooms}</p>
                                          </div>
                                          <div className="flex justify-start items-center gap-1 text-xs text-black/50">
                                            <MdOutlineViewModule />{" "}
                                            <p>Room View: {item.roomView}</p>
                                          </div>
                                          <div className="flex justify-start items-center gap-1 text-xs text-black/50">
                                            <SlDirections />{" "}
                                            <p>
                                              Room Direction:{" "}
                                              {item.roomDirection}
                                            </p>
                                          </div>
                                        </div>
                                        {item.isBreakFastIncluded === 1 && (
                                          <div className="flex justify-start items-center gap-1 text-xs text-black/50">
                                            <MdFastfood />{" "}
                                            <p>
                                              Complementary Breakfast included
                                            </p>
                                          </div>
                                        )}

                                        <div>
                                          <p className="text-xs text-black/50 text-justify">
                                            {item.description}
                                          </p>
                                        </div>
                                        <div>
                                          <p className="text-sm font-bold">
                                            Cancelation Policiy
                                          </p>
                                          <p className="text-xs text-black/50 text-justify">
                                            {item.cancelationPolicy}
                                          </p>
                                        </div>
                                        <div>
                                          <div>
                                            <p className="py-2 font-bold text-sm lg:text-md text-[#2155ad] capitalize bg-gradient-to-t from-blue-500 to-blue-900 bg-clip-text text-transparent">
                                              Facilities
                                            </p>
                                          </div>
                                          <div className="flex flex-wrap justify-start items-center gap-2">
                                            {item.facilities
                                              .slice(0, 2)
                                              .map((item, index) => {
                                                return (
                                                  <>
                                                    {item.facilities
                                                      .slice(0, 2)
                                                      .map((f, index) => {
                                                        return (
                                                          <div
                                                            key={index}
                                                            className="flex justify-start items-center gap-1"
                                                          >
                                                            <div className="w-4 h-4 overflow-hidden flex justify-center items-center">
                                                              <img
                                                                className="w-full h-full object-cover"
                                                                src={f.icon}
                                                                alt={
                                                                  f.facilityName
                                                                }
                                                              />
                                                            </div>
                                                            <div>
                                                              <p className="text-xs text-black/50">
                                                                {f.facilityName}
                                                              </p>
                                                            </div>
                                                          </div>
                                                        );
                                                      })}
                                                  </>
                                                );
                                              })}
                                            <div
                                              onClick={() =>
                                                handleOpenDrawer(item)
                                              }
                                            >
                                              <p className="text-xs text-blue-800 cursor-pointer select-none">
                                                view all amenties
                                              </p>
                                            </div>
                                          </div>
                                        </div>

                                        {/* ============================ */}
                                        {/* PER-NIGHT PRICE BREAKDOWN */}
                                        {/* Shown only when the stay has */}
                                        {/* different prices on different */}
                                        {/* dates. */}
                                        {/* ============================ */}
                                        {hasMixedPricing && (
                                          <div className="border border-amber-300 bg-amber-50 rounded-md p-3 space-y-2">
                                            <p className="text-xs font-semibold text-amber-800">
                                              Price changes during your stay
                                            </p>
                                            <div className="overflow-x-auto">
                                              <table className="w-full text-[11px] lg:text-xs">
                                                <thead>
                                                  <tr className="text-left text-black/50">
                                                    <th className="py-1 pr-2">
                                                      Dates
                                                    </th>
                                                    <th className="py-1 pr-2">
                                                      Nights
                                                    </th>
                                                    <th className="py-1 pr-2">
                                                      Per Night
                                                    </th>
                                                    <th className="py-1">
                                                      Subtotal
                                                    </th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {segments.map((seg, sidx) => (
                                                    <tr
                                                      key={sidx}
                                                      className="border-t border-amber-200"
                                                    >
                                                      <td className="py-1 pr-2">
                                                        {seg.startDate}
                                                        {seg.count > 1
                                                          ? ` → ${seg.endDate}`
                                                          : ""}
                                                      </td>
                                                      <td className="py-1 pr-2">
                                                        {seg.count}
                                                      </td>
                                                      <td className="py-1 pr-2">
                                                        {seg.discountedPrice.toFixed(
                                                          0,
                                                        )}{" "}
                                                        BDT
                                                        {seg.discount > 0 && (
                                                          <span className="text-[10px] text-green-600">
                                                            {" "}
                                                            ({seg.discount}%
                                                            off)
                                                          </span>
                                                        )}
                                                      </td>
                                                      <td className="py-1">
                                                        {seg.subtotal.toFixed(
                                                          0,
                                                        )}{" "}
                                                        BDT
                                                      </td>
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                            </div>
                                          </div>
                                        )}

                                        <div className="pt-3 flex justify-end items-end flex-col gap-3">
                                          <div className="space-y-3">
                                            {hasMixedPricing ? (
                                              // ---- MIXED PRICE: show computed total from breakdown ----
                                              <div className="flex justify-end items-end flex-col">
                                                <p className="font-bold text-xl">
                                                  {breakdown.subtotal.toFixed(
                                                    0,
                                                  )}
                                                  BDT
                                                </p>
                                                <p className="text-black/50 text-xs">
                                                  +
                                                  {breakdown.taxTotal.toFixed(
                                                    0,
                                                  )}{" "}
                                                  BDT Service Charge <br />
                                                  (All tax and fees are
                                                  included)
                                                </p>
                                                <p className="text-[11px] text-black/60 pt-1">
                                                  Total for{" "}
                                                  {breakdown.totalNights}{" "}
                                                  nights:{" "}
                                                  <span className="font-bold">
                                                    {breakdown.grandTotal.toFixed(
                                                      0,
                                                    )}{" "}
                                                    BDT
                                                  </span>
                                                </p>
                                              </div>
                                            ) : (
                                              // ---- UNIFORM PRICE: original single-price display ----
                                              <div className="flex justify-end items-end flex-col">
                                                <p className="w-fit self-end text-sm bg-[#ff034fe8] text-white p-1 rounded-md ">
                                                  {item.discount}% off
                                                </p>
                                                <p className="font-bold text-xl">
                                                  {(
                                                    item.actualPrice *
                                                    (1 -
                                                      (item.discount || 0) /
                                                        100) *
                                                    nights
                                                  ).toFixed(0)}
                                                  BDT
                                                </p>
                                                <p className=" text-md line-through text-red-500">
                                                  {(
                                                    item.actualPrice * nights
                                                  ).toFixed(0)}
                                                  BDT
                                                </p>
                                                <p className="text-black/50 text-xs">
                                                  +
                                                  {(
                                                    item.actualPrice *
                                                    (1 -
                                                      (item.discount || 0) /
                                                        100) *
                                                    ((item.tax || 0) / 100) *
                                                    nights
                                                  ).toFixed(0)}{" "}
                                                  BDT Service Charge <br />
                                                  (All tax and fees are
                                                  included)
                                                </p>
                                              </div>
                                            )}

                                            <div className="flex justify-end items-end">
                                              {unavailable ? (
                                                <button
                                                  disabled
                                                  className="px-4 py-2 text-sm rounded-md bg-gray-300 text-gray-500 cursor-not-allowed"
                                                >
                                                  Not Available
                                                </button>
                                              ) : (
                                                <AddButton
                                                  onClick={() =>
                                                    handleBook(item, breakdown)
                                                  }
                                                  text={
                                                    isAlreadyBooked
                                                      ? "+ Add More"
                                                      : "Book Now"
                                                  }
                                                />
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </>
                          ) : (
                            <div>
                              <p>No room available</p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="hidden lg:block w-full pt-5 lg:pt-0 lg:w-[300px] min-h-[300px] overflow-hidden rounded-lg ">
                        <BookedHotel />
                      </div>
                    </div>
                  </div>
                  <div className="p-3 rounded-lg">
                    <div>
                      <p className="py-3 font-bold text-xl text-[#2155ad] capitalize bg-gradient-to-t from-blue-500 to-blue-900 bg-clip-text text-transparent">
                        What's Near By
                      </p>
                    </div>
                    <div className="w-full flex-shrink-0 h-auto lg:h-[280px] overflow-hidden rounded-lg">
                      <Swiper
                        autoplay={{
                          delay: 5000,
                          disableOnInteraction: false,
                        }}
                        navigation={true}
                        modules={[Autoplay, Navigation]}
                        loop={true}
                        className="border"
                        breakpoints={{
                          320: {
                            slidesPerView: 1,
                            spaceBetween: 10,
                          },
                          640: {
                            slidesPerView: 2,
                            spaceBetween: 15,
                          },
                          768: {
                            slidesPerView: 2,
                            spaceBetween: 20,
                          },
                          1024: {
                            slidesPerView: 2,
                            spaceBetween: 20,
                          },
                          1280: {
                            slidesPerView: 2,
                            spaceBetween: 20,
                          },
                        }}
                      >
                        {HotelData?.nearBy.map((item, index) => {
                          return (
                            <SwiperSlide key={index}>
                              <div className="block lg:flex justify-start gap-3 p-3">
                                <div className="w-full lg:w-[250px] h-[220px] flex-shrink-0 overflow-hidden rounded-lg">
                                  <img
                                    className="w-full h-full object-cover"
                                    src={item.image}
                                    alt={HotelData.hotelName}
                                  />
                                </div>
                                <div>
                                  <div>
                                    <p className="py-2 font-bold text-xl text-[#2155ad] capitalize bg-gradient-to-t from-blue-500 to-blue-900 bg-clip-text text-transparent">
                                      {item.placeName}
                                    </p>
                                    <p className="text-xs text-black/50 text-justify">
                                      {item.description}
                                    </p>
                                  </div>
                                  <div>
                                    <div className="flex justify-start items-center gap-2 text-xs text-black/80 py-3">
                                      <div>
                                        <GiPathDistance />
                                      </div>
                                      <div>
                                        <p>
                                          Distance: {item.distance}/km from{" "}
                                          {item.neighbourhoodName}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </SwiperSlide>
                          );
                        })}
                      </Swiper>
                    </div>
                  </div>
                  <div className="p-3 rounded-lg">
                    <div>
                      <p className="py-3 font-bold text-xl text-[#2155ad] capitalize bg-gradient-to-t from-blue-500 to-blue-900 bg-clip-text text-transparent">
                        Hotel Facilities
                      </p>
                    </div>
                    <div className="border p-3 rounded-lg space-y-8 lg:space-y-5">
                      {HotelData?.facilities.map((item, index) => {
                        return (
                          <div key={index}>
                            <div className="space-y-1 lg:space-y-1">
                              <div>
                                <p className="text-sm font-bold">
                                  {item.useFor}
                                </p>
                              </div>
                              <div className="flex justify-start items-center gap-3 lg:gap-10 flex-wrap">
                                {item.facilities.map((item, index) => {
                                  return (
                                    <div
                                      key={index}
                                      className="flex justify-start items-center gap-1"
                                    >
                                      <div className="w-5 h-5 overflow-hidden">
                                        <img
                                          className="w-full h-full object-contain"
                                          src={item.icon}
                                          alt=""
                                        />
                                      </div>
                                      <div>
                                        <p className="text-xs">
                                          {item.facilityName}
                                        </p>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="hidden lg:block border p-3 rounded-lg">
                    <table className="w-full text-xs table-auto">
                      <tbody>
                        {[
                          {
                            icon: <FaCalendarCheck />,
                            label: "Check-in",
                            value: HotelData.checkIn,
                          },
                          {
                            icon: <FaCalendarTimes />,
                            label: "Check-out",
                            value: HotelData.checkOut,
                          },
                          {
                            icon: <MdOutlinePets />,
                            label: "Pet Allowed?",
                            value: HotelData.isPetAllowed
                              ? "Allowed"
                              : "Not Allowed",
                          },
                          ...(HotelData.isPetAllowed === 1
                            ? [
                                {
                                  icon: <FaCat />,
                                  label: "Pet Policy",
                                  value: (
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: HotelData.petPolicy,
                                      }}
                                    />
                                  ),
                                },
                              ]
                            : []),
                          {
                            icon: <FaHotel />,
                            label: "Hotel Rules",
                            value: (
                              <div
                                className="leading-7"
                                dangerouslySetInnerHTML={{
                                  __html: HotelData.hotelRules,
                                }}
                              />
                            ),
                          },
                          {
                            icon: <FaBabyCarriage />,
                            label: "Child Policy",
                            value: (
                              <div
                                className="leading-7"
                                dangerouslySetInnerHTML={{
                                  __html: HotelData.childPolicy,
                                }}
                              />
                            ),
                          },
                          {
                            icon: <MdPayment />,
                            label: "Payment Accepts",
                            value: (
                              <div className="flex items-center flex-wrap gap-2">
                                {HotelData.isAcceptMasterCard && (
                                  <img className="h-8" src={IcMasterCard} />
                                )}
                                {HotelData.isAcceptVisaCard && (
                                  <img className="h-8" src={IcVisacard} />
                                )}
                                {HotelData.isAcceptDebitCard && (
                                  <img className="h-8" src={IcDebit} />
                                )}
                                {HotelData.isAcceptAmmexCard && (
                                  <img className="h-8" src={IcAmmex} />
                                )}
                                {HotelData.isAcceptCash && (
                                  <img className="h-8" src={IcCash} />
                                )}
                              </div>
                            ),
                          },
                        ].map((item, idx) => (
                          <tr key={idx} className="align-top">
                            <td className="pr-5 w-[200px]">
                              <div className="flex items-start gap-2 p-3">
                                {item.icon}
                                <span>{item.label}</span>
                              </div>
                            </td>
                            <td>
                              <div className="p-3">{item.value}</div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="block lg:hidden border p-3 rounded-lg space-y-3">
                    {[
                      {
                        icon: <FaCalendarCheck />,
                        label: "Check-in",
                        value: HotelData.checkIn,
                      },
                      {
                        icon: <FaCalendarTimes />,
                        label: "Check-out",
                        value: HotelData.checkOut,
                      },
                      {
                        icon: <MdOutlinePets />,
                        label: "Pet Allowed?",
                        value: HotelData.isPetAllowed
                          ? "Allowed"
                          : "Not Allowed",
                      },
                      ...(HotelData.isPetAllowed === 1
                        ? [
                            {
                              icon: <FaCat />,
                              label: "Pet Policy",
                              value: (
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: HotelData.petPolicy,
                                  }}
                                />
                              ),
                            },
                          ]
                        : []),
                      {
                        icon: <FaHotel />,
                        label: "Hotel Rules",
                        value: (
                          <div
                            className="leading-7"
                            dangerouslySetInnerHTML={{
                              __html: HotelData.hotelRules,
                            }}
                          />
                        ),
                      },
                      {
                        icon: <FaBabyCarriage />,
                        label: "Child Policy",
                        value: (
                          <div
                            className="leading-7"
                            dangerouslySetInnerHTML={{
                              __html: HotelData.childPolicy,
                            }}
                          />
                        ),
                      },
                      {
                        icon: <MdPayment />,
                        label: "Payment Accepts",
                        value: (
                          <div className="flex items-center flex-wrap gap-2">
                            {HotelData.isAcceptMasterCard && (
                              <img className="h-8" src={IcMasterCard} />
                            )}
                            {HotelData.isAcceptVisaCard && (
                              <img className="h-8" src={IcVisacard} />
                            )}
                            {HotelData.isAcceptDebitCard && (
                              <img className="h-8" src={IcDebit} />
                            )}
                            {HotelData.isAcceptAmmexCard && (
                              <img className="h-8" src={IcAmmex} />
                            )}
                            {HotelData.isAcceptCash && (
                              <img className="h-8" src={IcCash} />
                            )}
                          </div>
                        ),
                      },
                    ].map((item, idx) => (
                      <div
                        key={idx}
                        className="flex gap-4 items-start p-3 border rounded-md bg-gray-50"
                      >
                        <div className="text-lg">{item.icon}</div>

                        <div>
                          <div className="font-semibold">{item.label}</div>
                          <div className="text-sm">{item.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </Container>
    </>
  );
};

export default Hotel;
